import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import {
  AccountClient,
  HttpAccountService,
  TransactionTypeEnum,
  TransactionTypeKey,
  checkSingleClient,
  displayClient,
  transactionTypesVal,
} from '@helios/common-api'
import {
  RootState,
  selectActualReconDate,
  setFocusedBulkExtTransactions,
} from '@helios/state-management'
import { getZeroUtcDate, splitNumbers } from '@helios/utils'
import {
  AssetIdListValidator,
  DateDiffValidator,
  IntegerIdValidator,
  dateDiffValidationMessages,
} from '@helios/validators'
import { Store } from '@ngrx/store'
import { DisplayDateFormat, toISOFormat } from '@northfork/basics'
import { Subject } from 'rxjs'
import { take } from 'rxjs/operators'
import {
  BULK_EXT_TRAN_LIMIT,
  BulkExtTranType,
  FindBulkExternalTransactionsForm,
  TranTypeSetValue,
} from '../../models/bulk-external-transactions.model'
import { BulkExternalTransactionsService } from '../../services/bulk-external-transactions-service/bulk-external-transactions.service'

@Component({
  selector: 'hs-find-transactions-popover',
  templateUrl: './find-transactions-popover.component.html',
  styleUrls: ['./find-transactions-popover.component.scss'],
})
export class FindTransactionsPopoverComponent implements OnInit, OnChanges {
  @Output() find = new EventEmitter<FindBulkExternalTransactionsForm>()

  @Input() bulkEditTranIds: number[] = []

  private readonly _destroyed$ = new Subject<void>()

  isFormSubmitting = false
  dateFormat = DisplayDateFormat
  bulkExtTranTypes: BulkExtTranType[] = []
  bulkTransactionStatus: string[] = []
  bulkDataSources: string[] = []
  hasSingleUltimateParent = false
  ultimateParentDisplay?: string
  ultimateParentId?: string
  ignoreUltParentCheck = false
  tranCountLimit = BULK_EXT_TRAN_LIMIT
  transactionLimitBreached = false

  dateRangeErrorMessages = { dateDiffValidationMessages }

  readonly rootIdType = new FormControl('account', [Validators.required])
  readonly rootIds = new FormControl('', [Validators.required])
  readonly startDate = new FormControl(null, [
    Validators.required,
    DateDiffValidator('endDate', 31),
  ])
  readonly endDate = new FormControl(getZeroUtcDate(new Date().toISOString()), [
    Validators.required,
    DateDiffValidator('startDate', 31),
  ])
  readonly ultimateParent = new FormControl('', [
    Validators.required,
    IntegerIdValidator,
  ])
  readonly assetIds = new FormControl('', [AssetIdListValidator])
  readonly transactionTypes = new FormControl([])
  readonly transactionStatus = new FormControl(
    ['UNMATCHED'],
    Validators.required,
  )
  readonly dataSources = new FormControl()

  form = new FormGroup({
    startDate: this.startDate,
    endDate: this.endDate,
    rootIdType: this.rootIdType,
    rootIds: this.rootIds,
    assetIds: this.assetIds,
    tranTypes: this.transactionTypes,
    transactionStatus: this.transactionStatus,
    dataSourceType: this.dataSources,
  })

  constructor(
    private readonly store: Store<RootState>,
    private readonly bulkService: BulkExternalTransactionsService,
    private readonly httpAccountService: HttpAccountService,
  ) {}

  async ngOnInit() {
    this.bulkService
      .getBulkExtTranSetValues()
      .pipe(take(1))
      .subscribe(setValues => {
        this.bulkTransactionStatus = setValues.status
        this.bulkDataSources = setValues.dataSourceType
        this.bulkExtTranTypes = setValues.tranTypes
          .map(tranType => ({
            name: TransactionTypeEnum[tranType.type],
            code: tranType.name,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
        this.selectAllForDropdownItems(this.bulkExtTranTypes)
      })

    this.store
      .select(selectActualReconDate)
      .pipe(take(1))
      .subscribe(date => {
        this.startDate.setValue(toISOFormat(date))
      })
  }

  ngOnChanges(): void {
    this.handleBulkEditFromBreaks(this.bulkEditTranIds)
  }

  ngAfterViewInit() {
    this.rootIds.valueChanges.subscribe(value => this.onRootIdsChange(value))
    this.rootIdType.valueChanges.subscribe(() =>
      this.onRootIdsChange(this.rootIds.value),
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAllForDropdownItems(items: any[]) {
    items.forEach(element => {
      element['selectedAllGroup'] = 'selectedAllGroup'
    })
  }

  getTransactionTypeKeys(codes: string[]): TransactionTypeKey[] {
    const typeList: TransactionTypeKey[] = []
    codes.forEach(c =>
      typeList.push(
        transactionTypesVal.find(val => val.code === c)?.type ?? 'UNSUPPORTED',
      ),
    )
    return typeList
  }

  onSubmit() {
    const tranTypeKeys = this.getTransactionTypeKeys(
      this.transactionTypes.value,
    )
    this.find.next({
      rootIdType: this.rootIdType.value,
      rootIds: this.rootIds.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
      assetIds: this.assetIds.value,
      tranTypes: tranTypeKeys,
      statuses: this.transactionStatus.value,
      dataSourceType: this.dataSources.value,
    })
    this.ignoreUltParentCheck = false
  }

  onStartDateSelect() {
    this.endDate.updateValueAndValidity()
  }

  onEndDateSelect() {
    this.startDate.updateValueAndValidity()
  }

  trackTranTypeByType(
    _index: number,
    item: TranTypeSetValue,
  ): TransactionTypeKey {
    return item.type
  }

  async onRootIdsChange(value: string) {
    this.transactionLimitBreached = false
    if (this.rootIdType.value === 'transaction') {
      this.hasSingleUltimateParent = true
      this.ignoreUltParentCheck = true
      this.startDate.disable()
      this.endDate.disable()
      const tranIds = splitNumbers(value)
      if (tranIds.length > this.tranCountLimit) {
        this.transactionLimitBreached = true
      }
      return
    }
    this.ignoreUltParentCheck = false
    const rootIds = splitNumbers(value)
    this.updateUltimateParent(rootIds)
    this.startDate.enable()
    this.endDate.enable()
  }

  async updateUltimateParent(rootIds: number[]) {
    const ultParents = await this.httpAccountService
      .getAccountUltimateParentClients(rootIds, this.rootIdType.value)
      .pipe(take(1))
      .toPromise()
    this.setUltimateParentClients(ultParents)
  }

  setUltimateParentClients(ultParents: AccountClient[]) {
    // Validation: IDs are within the same ultimate parent id
    this.hasSingleUltimateParent = checkSingleClient(ultParents)

    if (!this.hasSingleUltimateParent) {
      this.ultimateParentId = undefined
      this.ultimateParentDisplay = undefined
      return
    }

    const ultParent = ultParents[0]
    this.ultimateParentId = ultParent.id
    this.ultimateParentDisplay = displayClient(ultParent)
  }

  handleBulkEditFromBreaks(extTranIds: number[] | undefined) {
    if (!extTranIds || extTranIds.length === 0) return

    const tranIds = extTranIds?.join(',')
    this.rootIdType.setValue('transaction')
    this.rootIds.setValue(tranIds)

    setTimeout(() => {
      return this.store.dispatch(setFocusedBulkExtTransactions({ tranIds: [] }))
    }, 10)

    this.onRootIdsChange(tranIds)
  }
}
